import React, { useState, useMemo } from 'react';
import { SearchIcon, RefreshCw, Shield, Eye, User, AlertTriangle, Sparkles } from '../../icons';
import UserAIModal from './UserAIModal';
import Button from '../ui/Button';
import Sheet from '../ui/Sheet';
import Menu from '../ui/Menu';
import EmptyState from '../ui/EmptyState';
import ErrorDisplay from '../common/ErrorDisplay';
import { cn } from '../ui/cn';

const UserManagement = ({
  users,
  isLoading,
  error,
  onToggleAdmin,
  onUpdateUserAI,
  onImpersonate,
  onRefresh,
  currentUserId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, user: null });
  const [aiModalUser, setAiModalUser] = useState(null);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(user =>
      user.email?.toLowerCase().includes(query) ||
      user.displayName?.toLowerCase().includes(query) ||
      user.id.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const handleToggleAdmin = (user) => {
    if (user.id === currentUserId) {
      alert("You cannot remove your own admin status.");
      return;
    }
    // Show confirmation modal
    setConfirmModal({ show: true, user });
  };

  const confirmToggleAdmin = async () => {
    const user = confirmModal.user;
    if (!user) return;

    setConfirmModal({ show: false, user: null });
    setTogglingUserId(user.id);

    try {
      await onToggleAdmin(user.id, user.isAdmin);
    } catch (err) {
      alert(`Failed to update admin status: ${err.message}`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const cancelToggleAdmin = () => {
    setConfirmModal({ show: false, user: null });
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label="Loading users">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 bg-card border border-line rounded-card animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ErrorDisplay title="Error loading users" error={error} />
        <Button variant="secondary" onClick={onRefresh}>Retry</Button>
      </div>
    );
  }

  const Avatar = ({ user }) => (
    <div className={cn('w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0', user.isAdmin ? 'bg-accent-soft text-accent' : 'bg-card-2 text-secondary')}>
      {user.isAdmin ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
    </div>
  );

  const RoleBadge = ({ user }) => (
    <span className={cn('inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold', user.isAdmin ? 'bg-accent-soft text-accent' : 'bg-card-2 text-secondary')}>
      {user.isAdmin ? 'Admin' : 'User'}
    </span>
  );

  const userMenu = (user) => [
    { label: 'Manage AI provider/policy', icon: Sparkles, onSelect: () => setAiModalUser(user) },
    user.id !== currentUserId && { label: 'Impersonate user', icon: Eye, onSelect: () => onImpersonate(user.id, user.displayName, user.email) },
    {
      label: user.id === currentUserId ? 'Cannot modify your own admin status' : user.isAdmin ? 'Remove admin' : 'Make admin',
      icon: togglingUserId === user.id ? RefreshCw : Shield,
      danger: user.isAdmin && user.id !== currentUserId,
      disabled: togglingUserId === user.id || user.id === currentUserId,
      onSelect: () => handleToggleAdmin(user),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header with Search and Refresh */}
      <div className="flex gap-2 items-center">
        <label className="relative flex-1 min-w-0">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
          <input
            type="search"
            placeholder="Search by email, name, or ID…"
            aria-label="Search users"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 rounded-full"
          />
        </label>
        <Button variant="secondary" size="icon" onClick={onRefresh} aria-label="Refresh users">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="flex gap-2 text-xs text-secondary">
        <span>{users.length} total users</span>
        <span className="text-muted">·</span>
        <span>{users.filter(u => u.isAdmin).length} admins</span>
        {searchQuery && (
          <>
            <span className="text-muted">·</span>
            <span>{filteredUsers.length} matching</span>
          </>
        )}
      </div>

      {/* Confirmation */}
      <Sheet isOpen={confirmModal.show && !!confirmModal.user} onClose={cancelToggleAdmin} mode="dialog" size="sm" title="Confirm admin change">
        {confirmModal.user && (
          <>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-amber-soft text-amber flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </span>
              <p className="text-sm text-primary">
                Are you sure you want to {confirmModal.user.isAdmin ? 'remove' : 'grant'} admin privileges for:
              </p>
            </div>
            <div className="bg-page border border-line rounded-xl p-3 my-3">
              <div className="font-medium text-primary">{confirmModal.user.displayName}</div>
              <div className="text-sm text-secondary break-all">{confirmModal.user.email || confirmModal.user.id}</div>
            </div>
            <p className="text-xs text-secondary">
              {confirmModal.user.isAdmin
                ? 'This user will lose access to the admin panel and all admin features.'
                : 'This user will gain full access to the admin panel, user management, and app settings.'}
            </p>
            <div className="flex gap-2 justify-end mt-5">
              <Button variant="secondary" onClick={cancelToggleAdmin}>Cancel</Button>
              <Button variant={confirmModal.user.isAdmin ? 'danger-solid' : 'primary'} onClick={confirmToggleAdmin}>
                {confirmModal.user.isAdmin ? 'Remove admin' : 'Make admin'}
              </Button>
            </div>
          </>
        )}
      </Sheet>

      {/* Per-user AI management modal */}
      {aiModalUser && (
        <UserAIModal
          user={aiModalUser}
          onClose={() => setAiModalUser(null)}
          onSave={onUpdateUserAI}
        />
      )}

      {filteredUsers.length === 0 ? (
        <EmptyState icon={User} title={searchQuery ? 'No users match your search' : 'No users found'} />
      ) : (
        <>
          {/* Mobile: card list */}
          <ul className="lg:hidden space-y-2">
            {filteredUsers.map((user) => (
              <li key={user.id} className="bg-card border border-line rounded-card p-3.5 flex items-center gap-3">
                <Avatar user={user} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-primary truncate">{user.displayName}</span>
                    {user.id === currentUserId && <span className="text-[11px] text-accent">(You)</span>}
                    <RoleBadge user={user} />
                  </div>
                  <div className="text-xs text-secondary truncate">{user.email || user.id}</div>
                  <div className="text-xs text-muted mt-0.5 tabular">
                    {user.propertyCount} properties · {user.searchCount} searches · {formatDate(user.lastLoginAt)}
                  </div>
                </div>
                <Menu items={userMenu(user)} label={`Actions for ${user.displayName}`} />
              </li>
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="hidden lg:block bg-card border border-line rounded-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 text-center">Properties</th>
                  <th className="px-4 py-3 text-center">Searches</th>
                  <th className="px-4 py-3">Last active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-line last:border-0 hover:bg-white/[0.03] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar user={user} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-primary">
                            {user.displayName}
                            {user.id === currentUserId && <span className="ml-2 text-xs text-accent">(You)</span>}
                          </div>
                          <div className="text-xs text-secondary">{user.email || user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><RoleBadge user={user} /></td>
                    <td className="px-4 py-3 text-center text-sm text-primary tabular">{user.propertyCount}</td>
                    <td className="px-4 py-3 text-center text-sm text-primary tabular">{user.searchCount}</td>
                    <td className="px-4 py-3 text-sm text-secondary whitespace-nowrap">{formatDate(user.lastLoginAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setAiModalUser(user)}
                          className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-accent hover:bg-accent-soft"
                          title="Manage AI provider/policy"
                          aria-label="Manage AI provider/policy"
                        >
                          <Sparkles className="w-4 h-4" />
                        </button>
                        {user.id !== currentUserId && (
                          <button
                            type="button"
                            onClick={() => onImpersonate(user.id, user.displayName, user.email)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-amber hover:bg-amber-soft"
                            title="Impersonate user"
                            aria-label="Impersonate user"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleAdmin(user)}
                          disabled={togglingUserId === user.id || user.id === currentUserId}
                          className={cn(
                            'w-9 h-9 flex items-center justify-center rounded-lg',
                            user.id === currentUserId
                              ? 'text-muted cursor-not-allowed'
                              : user.isAdmin
                                ? 'text-accent hover:text-danger hover:bg-danger-soft'
                                : 'text-secondary hover:text-accent hover:bg-accent-soft'
                          )}
                          title={user.id === currentUserId ? 'Cannot modify your own admin status' : user.isAdmin ? 'Remove admin' : 'Make admin'}
                          aria-label={user.id === currentUserId ? 'Cannot modify your own admin status' : user.isAdmin ? 'Remove admin' : 'Make admin'}
                        >
                          {togglingUserId === user.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;
